import { cn } from '../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText } from '../../../../../styling/stylingHelper';
import type {
  AICheckerTrace,
  ConsolidatedDeepCheckResult,
  MixedSupplierFileCheck,
} from '../../../../../services/admin/adminService';
import type { TFn } from '../types';

/**
 * Detail view for the mixed-suppliers deep check — an email bundling invoice PDFs from DIFFERENT
 * suppliers, where the single-supplier "all files" check would report every other supplier's files as
 * missing. Per file the supplier is detected from the invoice text and its numbers are looked up
 * platform-wide (orders + facturen), then files split into found / not-found sections.
 * The check itself runs on the audit row (`runRowAction`) — this only renders the result it produced,
 * so opening/closing the modal never re-runs (and never re-pays for) the AI calls.
 */
export function MixedCheckModal({ trace, result, onClose, t }: { trace: AICheckerTrace; result: ConsolidatedDeepCheckResult; onClose: () => void; t: TFn }) {
  const files = result?.mixedFiles || [];
  const found = files.filter((f) => f.found);
  const notFound = files.filter((f) => !f.found);

  const supplierLabel = (f: MixedSupplierFileCheck) => f.platform || f.supplierName || t('adminTesting.mixedCheck.unknownSupplier');

  const fileCard = (f: MixedSupplierFileCheck) => (
    <div key={f.filename} className={cn('p-2 rounded-md border', 'border-gray-200 dark:border-gray-700')}>
      <div className="flex items-center justify-between gap-2">
        <p className={cn(primaryText, 'text-xs font-mono truncate')}>{f.filename}</p>
        <span className={cn(secondaryText, 'text-[10px] shrink-0')}>{supplierLabel(f)}</span>
      </div>
      {f.referenced.length === 0 ? (
        <p className="text-[10px] text-amber-600 dark:text-amber-400">{t('adminTesting.mixedCheck.noNumbers')}</p>
      ) : (
        <>
          {f.matches.map((m) => (
            <p key={m.number} className={cn(secondaryText, 'text-[10px]')}>
              {m.number} · {t(m.source === 'order' ? 'adminTesting.mixedCheck.matchOrder' : 'adminTesting.mixedCheck.matchFactuur')} · {m.platform} · {m.restaurantName}
            </p>
          ))}
          {f.missing.length > 0 && (
            <p className={cn('text-[10px]', f.found ? 'text-amber-600 dark:text-amber-400' : 'text-red-500')}>
              {t('adminTesting.mixedCheck.missingNumbers', { list: f.missing.join(', ') })}
            </p>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={onClose}>
      <div className={cn(cardClass, 'w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.mixedCheck.title')}</h3>
            <p className={cn(secondaryText, 'text-xs mt-0.5')}>{trace.emailSubject}</p>
          </div>
          <button onClick={onClose} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
        </div>

        <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.mixedCheck.description')}</p>

        {files.length === 0 && (
          <p className="text-xs text-red-500 dark:text-textRed-dark">{result.reason || t('adminTesting.review.deepCheckFailed')}</p>
        )}

        {files.length > 0 && (
          <div className="space-y-3">
            <p className={cn(primaryText, semiBoldText, 'text-xs')}>
              {t('adminTesting.mixedCheck.summary', { found: found.length, total: files.length })}
            </p>
            {found.length > 0 && (
              <div className="space-y-1.5">
                <p className={cn('text-xs', semiBoldText, 'text-green-600 dark:text-green-400')}>{t('adminTesting.mixedCheck.foundSection', { count: found.length })}</p>
                {found.map(fileCard)}
              </div>
            )}
            {notFound.length > 0 && (
              <div className="space-y-1.5">
                <p className={cn('text-xs', semiBoldText, 'text-red-500 dark:text-textRed-dark')}>{t('adminTesting.mixedCheck.notFoundSection', { count: notFound.length })}</p>
                {notFound.map(fileCard)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
